import React, { useState, useEffect, useCallback, useContext } from "react";
import FileViewer from "../components/filesviewpage/FileViewer";
import FileDetails from "../components/filesviewpage/FileDetails";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import useAuth from "../stores/authStore";
import { auth, storage } from "../helper/firebaseClient";
import { api } from "../utils/axios-instance";
import axios from "axios";
import Loading from "../components/Loading";
import fileContext from "../context/fileContext";
import { useLocation } from "react-router-dom";
import { ref, uploadBytesResumable } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import toast, { Toaster } from "react-hot-toast";

const FileView = ({
  fileInfo,
  closeDrawer,
  sharedFileInfo,
  embedded = false,
  showComments = false,
}) => {
  // Defensive no-op: FileView is used in contexts where closeDrawer may not
  // be provided (e.g. FilesInChat before the fix propagates). Never crash.
  const safeCloseDrawer = useCallback(() => {
    if (typeof closeDrawer === "function") closeDrawer();
  }, [closeDrawer]);

  // Destructure stable primitives so useCallback/useEffect dep arrays
  // reference values, not property accesses on a potentially-changing object.
  const fileId = fileInfo?.id;
  const fileMimetype = fileInfo?.mimetype;
  const fileCurrVersion = fileInfo?.currVersion;
  const fileDownloadUrl = fileInfo?.download_url;
  const fileDownloadUrlAlt = fileInfo?.downloadUrl;
  const filePreviewUrl = fileInfo?.preview_url;
  const CACHE_NAME = "blob-cache";
  const MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000; // Cache duration in milliseconds (1 week)

  const screenshotDetected = useAuth((state) => state.screenshotDetected);
  const screenshotAlert = useAuth((state) => state.screenshotAlert);
  const profileData = useAuth((state) => state.profileData);

  const [isOpen, setIsOpen] = useState(false);
  const [preUrl, setPreUrl] = useState("");
  const [signedUrl, setSignedUrl] = useState("");
  const [loadingUrl, setLoadingUrl] = useState(true);
  const [saving, setSaving] = useState(false);

  // Always default to {} — NEVER null/undefined.
  // TextEditor and other child components destructure this object directly,
  // so passing null causes "Cannot read properties of null" crashes.
  const [securityDetails, setSecurityDetails] = useState({});
  const location = useLocation();

  const context = useContext(fileContext);

  const openDialog = () => setIsOpen(true);

  const closeDialog = () => {
    setIsOpen(false);
    if (typeof context.setCurrFileFor === "function") {
      context.setCurrFileFor(fileId, null);
    }
    context.setCurrFile(null);
    safeCloseDrawer();
  };

  useEffect(() => {
    if (embedded) {
      const userId = profileData?.id || "client";
      setSecurityDetails({
        download_enabled: false,
        has_password: false,
        is_locked: false,
        allowEdit: false,
        selective_access: {
          [userId]: {
            access: ["view"],
          },
        },
      });
    }
  }, [embedded, profileData?.id]);

  useEffect(() => {
    if (screenshotDetected) {
      screenshotAlert(fileId);
    }
  }, [screenshotDetected, fileId, screenshotAlert]);

  useEffect(() => {
    openDialog();

    // Log the file access
    const logAccess = async (retries = 0) => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          if (retries < 5) {
            setTimeout(() => logAccess(retries + 1), 500);
          }
          return;
        }
        const token = await currentUser.getIdToken();
        const res = await api.post(
          `/file/logEvent/${fileId}?event=file_access`,
          {
            event: "file_access",
            idToken: token,
          }
        );
        if (res.status === 200 || res.status === 201) {
          toast.success("File view logged successfully!");
        }
      } catch (error) {
        console.warn("Failed to log file access:", error);
        // Deliverable files opened via an embedded preview don't always
        // have a matching org file record, so the backend returns 400 —
        // that's expected here and shouldn't surface as a user-facing error.
        if (error?.response?.status === 400) {
          return;
        }
        toast.error("Failed to log file access: " + error?.message);
      }
    };

    if (fileId) {
      logAccess();
    }
  }, [fileId]);

  const getCacheEntry = useCallback(
    async (fileIdentifier) => {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(fileIdentifier);
      if (!response) return null;

      const dateHeader = response.headers.get("sw-cache-date");
      if (!dateHeader) return null;

      const cacheTime = new Date(dateHeader).getTime();
      const currentTime = Date.now();

      if (currentTime - cacheTime > MAX_CACHE_AGE) {
        await cache.delete(fileIdentifier);
        return null;
      }

      return response;
    },
    [MAX_CACHE_AGE]
  );

  const getPresignedUrl = useCallback(async () => {
    try {
      setLoadingUrl(true);

      const token = await auth.currentUser.getIdToken();

      const cache = await caches.open(CACHE_NAME);

      if (!fileId) {
        console.error("Missing fileInfo.id", fileInfo);
        toast.error("Cannot open file. Missing file ID.");
        return;
      }

      const fileIdentifier = `${fileId}-${token}`;

      const body = {
        latitude: 18.44623721673684,
        longitude: 73.82762833796289,
        idToken: token,
      };

      if (fileCurrVersion && fileCurrVersion !== "") {
        body["version"] = fileCurrVersion;
      }

      const serverResponse = await api.post(
        `/file/getPresigned/${fileId}/`,
        body
      );

      // Always use an object fallback — never let securityDetails become null.
      // The server returns null when there are no security settings on the file.
      // Child components (TextEditor, SpreadsheetComponent, etc.) will crash
      // if they try to read keys off null.
      const rawSecurity =
        serverResponse.data.security_details ||
        serverResponse.data.security ||
        null;
      setSecurityDetails(rawSecurity ?? {});

      // setSecurityDetails(serverResponse.data.security_details || null);

      const fileVersionId = serverResponse.data.versionId || null;
      const url = serverResponse.data.signed_url;

      setSignedUrl(url);

      // Check if the file is already in the cache
      const cachedResponse = await getCacheEntry(fileIdentifier);
      if (cachedResponse) {
        // console.log("Cache Found");
        const cacheVersionId = cachedResponse.headers.get("x-file-version-id");

        if (cacheVersionId && cacheVersionId === fileVersionId) {
          const cachedBlob = await cachedResponse.blob();
          setPreUrl(URL.createObjectURL(cachedBlob));
          return;
        } else {
          await cache.delete(fileIdentifier);
        }
      }

      // Cache miss: Fetch new data from server
      try {
        const response = await axios.get(url, { responseType: "arraybuffer" });

        const contentType =
          response.headers["content-type"] ||
          fileMimetype ||
          "application/octet-stream";

        const blob = new Blob([response.data], { type: contentType });

        // Cache the new file
        const responseToCache = new Response(blob, {
          headers: {
            "Content-Type": response.headers["content-type"],
            "sw-cache-date": new Date().toISOString(),
            "x-file-version-id": fileVersionId,
          },
        });
        await cache.put(fileIdentifier, responseToCache);

        const dataUrl = URL.createObjectURL(blob);
        setPreUrl(dataUrl);
      } catch (err) {
        const statusCode = err?.response?.status;

        console.error("Signed URL fetch failed:", {
          message: err?.message,
          status: statusCode,
          data: err?.response?.data,
        });

        if (statusCode === 404) {
          toast.error(
            "File not found in storage (404). Backend signed wrong object path."
          );
          setPreUrl("");
          return;
        }

        setPreUrl(url);
      }
    } catch (error) {
      console.error("Error while fetching presigned URL:", {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
    } finally {
      setLoadingUrl(false);
    }
  }, [fileId, fileCurrVersion, fileMimetype, getCacheEntry]);

  useEffect(() => {
    // The caller already resolved a usable URL for this file (e.g. the
    // client/vendor deliverable review flows, whose backend response
    // includes preview_url or download_url directly) — use it as-is
    // instead of calling get_presigned(), which assumes a vault-linked
    // file record that deliverables don't always have.
    const directUrl = filePreviewUrl || fileDownloadUrl || fileDownloadUrlAlt;

    if (directUrl) {
      setPreUrl(directUrl);
      setSignedUrl(directUrl);
      setLoadingUrl(false);
      return;
    }

    getPresignedUrl();
  }, [fileId, getPresignedUrl, fileDownloadUrl, fileDownloadUrlAlt, filePreviewUrl]);

  if (loadingUrl) {
    return <Loading />;
  }

  // Guard against a missing/incomplete file record so FileDetails and the
  // viewer overlay below never read properties (fileInfo.mimetype, .name,
  // .id, ...) off a null/undefined fileInfo.
  if (!fileInfo || !fileInfo.id) {
    return (
      <div className="text-center pt-20">Deliverable file is unavailable.</div>
    );
  }

  const formatDate = (date) => {
    const day = date.getDate().toString().padStart(2, "0"); // Day of the month
    const month = date.toLocaleString("default", { month: "short" }); // Short month name
    const year = date.getFullYear(); // Full year

    let hours = date.getHours(); // Hour in 24-hour format
    const minutes = date.getMinutes().toString().padStart(2, "0"); // Minutes with leading zero
    const period = hours >= 12 ? "pm" : "am"; // am/pm

    // Convert to 12-hour format
    hours = hours % 12 || 12;

    return `${day} ${month}, ${year}\n${hours}:${minutes}${period}`;
  };

  const saveVersion = () => {
    const date = formatDate(new Date());
    return new Promise(async (resolve, reject) => {
      const id = uuidv4();
      const fileRef = ref(storage, `files/${profileData.org_id}/${id}`);

      const fileForUpload =
        (typeof context.getCurrFileFor === "function" &&
          context.getCurrFileFor(fileId)) ||
        context.currFile;

      const uploadTask = uploadBytesResumable(fileRef, fileForUpload);

      uploadTask.on(
        "state_changed",
        () => {},
        (error) => {
          console.error("File upload error:", error);
          reject(error);
        },
        async () => {
          console.log("File version saved successfully");
          resolve();
        }
      );

      const token = await auth.currentUser.getIdToken();
      const versionData = {
        id: id,
        lastUpdate: date,
      };

      await api.post(`file/save-file-version/${fileInfo.id}/`, {
        versionData: versionData,
        idToken: token,
      });

      context.setFilteredData((prevData) =>
        prevData.map((data) =>
          data.id === fileInfo.id
            ? { ...data, versions: [...data.versions, versionData] }
            : data
        )
      );

      toast.success("Version saved successfully");
    });
  };

  return (
    <div>
      <Toaster reverseOrder={false} position="bottom-left" />
      {!context.isMultiSelect && location.pathname !== "/chat" && !embedded ? (
        <Dialog
          open={isOpen}
          onClose={closeDialog}
          fullScreen
          disableEscapeKeyDown
        >
          <DialogContent
            style={{
              backgroundColor: "#F7F8FA",
              margin: 0,
              padding: 0,
            }}
          >
            <div className={`flex flex-col`}>
              <div className="w-full">
                <FileDetails
                  fileInfo={fileInfo}
                  sharedFileInfo={sharedFileInfo}
                  closeDrawer={safeCloseDrawer}
                  preUrl={preUrl}
                  signedUrl={signedUrl}
                  saving={saving}
                  setPreUrl={setPreUrl}
                  securityDetails={securityDetails}
                  saveVersion={saveVersion}
                  embedded={embedded}
                />
              </div>
              <div className={`w-full ${screenshotDetected ? "blur" : ""}`}>
                {loadingUrl && (
                  <div className="text-center pt-20">Fetching URL...</div>
                )}
                {!loadingUrl && preUrl ? (
                  <FileViewer
                    preUrl={preUrl}
                    mimetype={fileInfo.mimetype}
                    signedUrl={signedUrl}
                    fileName={fileInfo.name}
                    fileId={fileInfo.id}
                    setSaving={setSaving}
                    saving={saving}
                    securityDetails={securityDetails}
                    currVersion={fileInfo.currVersion}
                    embedded={embedded}
                    showComments={showComments}
                  />
                ) : (
                  <div className="text-center pt-20">
                    You do not have access to the file.
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <div className="flex flex-col bg-white w-full h-full">
          <div>
            <FileDetails
              fileInfo={fileInfo}
              sharedFileInfo={sharedFileInfo}
              closeDrawer={safeCloseDrawer}
              preUrl={preUrl}
              signedUrl={signedUrl}
              saving={saving}
              securityDetails={securityDetails}
              currVersion={fileInfo.currVersion}
              embedded={embedded}
            />
          </div>
          <div className={`w-full ${screenshotDetected ? "blur" : ""}`}>
            {loadingUrl && (
              <div className="text-center pt-20">Fetching URL...</div>
            )}
            {!loadingUrl && preUrl ? (
              <FileViewer
                preUrl={preUrl}
                mimetype={fileInfo.mimetype}
                signedUrl={signedUrl}
                fileName={fileInfo.name}
                fileId={fileInfo.id}
                setSaving={setSaving}
                securityDetails={securityDetails}
                currVersion={fileInfo.currVersion}
                embedded={embedded}
                showComments={showComments}
              />
            ) : (
              <div className="text-center pt-20">
                You do not have access to the file.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileView;
